import React, { useState, useEffect, useCallback } from 'react';
import { GoogleMap, LoadScript, Marker, InfoWindow } from '@react-google-maps/api';
import PostDetailModal from './PostDetailModal';
import { mapsAPI } from '../utils/api';

const containerStyle = {
  width: '100%',
  height: '100%',
};

const defaultCenter = {
  lat: 10.8231, // Ho Chi Minh City
  lng: 106.6297,
};

const MapComponent = ({ onSelectPost, posts: tripPosts, showTripRoute = false }) => {
  const [spots, setSpots] = useState([]);
  const [selectedSpot, setSelectedSpot] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load camping spots (only if not showing trip route)
  useEffect(() => {
    if (!showTripRoute) {
      loadSpots();
    }
  }, [showTripRoute]);

  const loadSpots = async () => {
    try {
      setLoading(true);
      const response = await mapsAPI.getSpots();
      setSpots(response.data);
      setError(null);
    } catch (err) {
      console.error('Error loading spots:', err);
      setError('Failed to load camping spots');
    } finally {
      setLoading(false);
    }
  };

  const handleMapClick = useCallback((event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    console.log('Map clicked at:', lat, lng);
    // TODO: Allow creating new spot
  }, []);

  const handleMarkerClick = (spot) => {
    setSelectedSpot(spot);
    // Load posts for this spot
    loadSpotPosts(spot.id);
  };

  const loadSpotPosts = async (spotId) => {
    try {
      // Find the spot from the spots array
      const spot = spots.find(s => s.id === spotId);
      if (spot) {
        onSelectPost && onSelectPost(spot);
      }
    } catch (err) {
      console.error('Error loading posts:', err);
    }
  };

  const handleMarkerClose = () => {
    setSelectedSpot(null);
  };

  const handlePostClick = (post) => {
    setSelectedPost(post);
  };

  const handleCloseModal = () => {
    setSelectedPost(null);
  };

  // Create trip post marker with order number
  const createTripPostMarker = (order) => {
    return {
      path: 'M 0,0 C -6,0 -6,-8 0,-12 C 6,-8 6,0 0,0 z',
      fillColor: '#FF5722',
      fillOpacity: 1,
      strokeWeight: 2,
      strokeColor: '#FFFFFF',
      scale: 1.2,
      label: order + 1
    };
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100%',
        fontSize: '18px',
        color: '#666'
      }}>
        Loading map...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100%',
        fontSize: '18px',
        color: '#c62828'
      }}>
        {error}
      </div>
    );
  }

  return (
    <LoadScript
      googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'YOUR_API_KEY_HERE'}
    >
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={defaultCenter}
        zoom={12}
        onClick={handleMapClick}
        options={{
          streetViewControl: false,
          mapTypeControl: true,
          fullscreenControl: false,
          styles: [
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }]
            }
          ]
        }}
      >
        {/* Trip posts markers (when showing trip route) */}
        {showTripRoute && tripPosts && tripPosts.map((post, index) => (
          <Marker
            key={`trip-post-${post.id}`}
            position={{ lat: post.latitude, lng: post.longitude }}
            onClick={() => handlePostClick(post)}
            icon={createTripPostMarker(index)}
            title={`Post ${index + 1}: ${post.content?.substring(0, 30) || 'No content'}`}
            label={`${index + 1}`}
          />
        ))}

        {/* Camping spot markers (when not showing trip route) */}
        {!showTripRoute && spots.map((spot) => (
          <Marker
            key={spot.id}
            position={{ lat: spot.latitude, lng: spot.longitude }}
            onClick={() => handleMarkerClick(spot)}
            icon={{
              path: 'M 0,0 C -5,0 -5,-7 0,-10 C 5,-7 5,0 0,0 z',
              fillColor: '#4285F4',
              fillOpacity: 1,
              strokeWeight: 2,
              strokeColor: '#FFFFFF',
              scale: 1.5,
            }}
            title={spot.title}
          />
        ))}

        {/* Info window for selected spot */}
        {selectedSpot && (
          <InfoWindow
            position={{ lat: selectedSpot.latitude, lng: selectedSpot.longitude }}
            onCloseClick={handleMarkerClose}
          >
            <div style={{ padding: '10px', minWidth: '200px' }}>
              <h3 style={{ margin: '0 0 5px 0', fontSize: '16px' }}>
                {selectedSpot.title}
              </h3>
              <p style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#666' }}>
                {selectedSpot.description.substring(0, 100)}
                {selectedSpot.description.length > 100 ? '...' : ''}
              </p>
              <button
                onClick={() => {
                  // Open post detail modal with spot's top post
                  handlePostClick({ campingSpotId: selectedSpot.id });
                }}
                style={{
                  backgroundColor: '#4285F4',
                  color: 'white',
                  border: 'none',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '13px'
                }}
              >
                View Posts ({selectedSpot.postCount})
              </button>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>

      {/* Post detail modal */}
      {selectedPost && (
        <PostDetailModal
          post={selectedPost}
          onClose={handleCloseModal}
        />
      )}
    </LoadScript>
  );
};

export default MapComponent;
